/**
 * Pure helpers behind the PHONE timeline of Production Records
 * (plan-06-mobile.md, lane A). The phone renders a different component tree
 * from the desktop (`components/production/mobile/`): a Road Warrior card
 * timeline whose card wears its status as a Badge in the top-right corner,
 * and a filter bar whose `Filters` button counts the filters that moved off
 * their defaults.
 *
 * Side-effect free; unit-tested in
 * `client/test/lib/production-records/mobile-timeline.test.ts`.
 */

import type { FilterState } from '@/api/production-records'
import type { DayProgress } from '@/lib/production-records/day-progress'

/**
 * How many filters are off their defaults: the date window (default: past
 * 30 days), any mealtime picked, any campus picked. The campus filter counts
 * only on a school that HAS a campus filter (two or more campuses); a stale
 * campus set on a single-campus school changes nothing on screen and must
 * not show a count for it.
 */
export function activeFilterCount(
  state: Pick<FilterState, 'dateWindow' | 'mealtimes' | 'campuses'>,
  hasCampuses: boolean,
): number {
  let count = 0
  if (state.dateWindow !== 'last30') {
    count += 1
  }
  if (state.mealtimes.size > 0) {
    count += 1
  }
  if (hasCampuses && state.campuses.size > 0) {
    count += 1
  }
  return count
}

export type MobileStatusVariant = 'success' | 'warning' | 'secondary'

export interface MobileStatusBadge {
  label: string
  variant: MobileStatusVariant
}

/**
 * The card's status Badge: green `Complete`, amber with the progress in words
 * (`Not started`, `In progress`, `67% complete`) while open, and a quiet
 * `Records pending` while the materializer has not generated the records yet
 * (nothing on the day can be opened, so nothing is amber).
 */
export function mobileStatusBadge(
  progress: Pick<DayProgress, 'complete' | 'awaitingRecords' | 'statusText'>,
): MobileStatusBadge {
  if (progress.complete) {
    return { label: 'Complete', variant: 'success' }
  }
  if (progress.awaitingRecords) {
    return { label: 'Records pending', variant: 'secondary' }
  }
  return { label: progress.statusText, variant: 'warning' }
}

/** `data-state` of a mobile card, the same three values `DayCard` uses. */
export function mobileCardState(
  progress: Pick<DayProgress, 'complete' | 'awaitingRecords'>,
): 'complete' | 'awaiting' | 'open' {
  return progress.complete ? 'complete' : progress.awaitingRecords ? 'awaiting' : 'open'
}

/**
 * The detail line under the card's rule: who signed on a complete day
 * (`Complete` when the rollup carries no signature), the entry count while
 * open (`2 of 3 entries done`), or why nothing can be opened yet.
 */
export function mobileDetailLine(
  progress: Pick<DayProgress, 'complete' | 'statusText' | 'statusDetail'>,
  signedLine: string | null,
): string {
  if (progress.complete) {
    return signedLine ?? 'Complete'
  }
  return progress.statusDetail ?? progress.statusText
}
